import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, signal, WritableSignal } from '@angular/core';
import { Subject, takeUntil, catchError, timeout } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { of } from 'rxjs';
import { environment } from 'src/environments/environment';

interface BiblePassage {
  reference: string;
  content: string;
}

interface StaticVerse {
  reference: string;
  content: string;
}

interface BibleApiResponse {
  data: {
    passages: BiblePassage[];
  };
  meta: {
    fumsId: string;
  };
}

@Component({
  selector: 'app-verse-of-the-day',
  standalone: false,
  templateUrl: './verse-of-the-day.component.html',
  styleUrl: './verse-of-the-day.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VerseOfTheDayComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject<void>();
  
  // API Configuration
  private readonly API_KEY = environment.config.API_BIBLE_API_KEY;
  private readonly BIBLE_ID = '32664dc3288a28df-03'; // Using the Bible ID from the documentation
  private readonly API_BASE_URL = 'https://api.scripture.api.bible/v1';
  
  // Verse selection - using date-based random seed for daily random verse selection
  // Following API.Bible tutorial approach: same verse throughout each day, random each new day
  private readonly VERSES = [
    'JER.29.11',    // "For I know the plans I have for you," declares the LORD
    'PSA.23',       // The LORD is my shepherd
    '1CO.13.4-8',   // Love is patient, love is kind
    'PHP.4.13',     // I can do all things through Christ
    'JHN.3.16',     // For God so loved the world
    'ROM.8.28',     // And we know that in all things God works
    'ISA.41.10',    // So do not fear, for I am with you
    'PSA.46.1',     // God is our refuge and strength
    'GAL.5.22-23',  // But the fruit of the Spirit is
    'HEB.11.1',     // Now faith is confidence in what we hope for
    '2TI.1.7',      // For the Spirit God gave us does not make us timid
    '1CO.10.13',    // No temptation has overtaken you except what is common
    'PRO.22.6',     // Start children off on the way they should go
    'ISA.40.31',    // But those who hope in the LORD will renew their strength
    'JOS.1.9',      // Have I not commanded you? Be strong and courageous
    'HEB.12.2',     // Fixing our eyes on Jesus, the pioneer and perfecter of faith
    'MAT.11.28',    // Come to me, all you who are weary and burdened
    'ROM.10.9-10',  // If you declare with your mouth, "Jesus is Lord"
    'PHP.2.3-4',    // Do nothing out of selfish ambition or vain conceit
    'MAT.5.43-44',  // You have heard that it was said, "Love your neighbor"
    'PSA.119.105',  // Your word is a lamp for my feet
    'JHN.14.6',     // Jesus answered, "I am the way and the truth and the life"
    'COL.3.23',     // Whatever you do, work at it with all your heart
    '1PE.5.7',      // Cast all your anxiety on him because he cares for you
    'PSA.139.14',   // I praise you because I am fearfully and wonderfully made
    'ISA.43.1',     // But now, this is what the LORD says
    'MAT.28.19-20', // Therefore go and make disciples of all nations
    'PSA.27.1',     // The LORD is my light and my salvation
    'JHN.1.1',      // In the beginning was the Word
    'ROM.12.2',     // Do not conform to the pattern of this world
    '1JN.4.19',     // We love because he first loved us
    'PSA.100.4',    // Enter his gates with thanksgiving
    'MAT.6.33',     // But seek first his kingdom and his righteousness
    'PSA.19.14',    // May these words of my mouth and this meditation of my heart
    'PRO.3.5-6',    // Trust in the LORD with all your heart
    'ISA.55.8-9',   // "For my thoughts are not your thoughts"
    'JHN.15.5',     // "I am the vine; you are the branches"
    'PSA.51.10',    // Create in me a pure heart, O God
    '2CO.5.17',     // Therefore, if anyone is in Christ
    'EPH.2.8-9',    // For it is by grace you have been saved
    'PHP.4.6-7'     // Do not be anxious about anything
  ];

  // Static verses as fallback when API fails
  private readonly STATIC_VERSES: StaticVerse[] = [
    {
      reference: "Jeremiah 29:11",
      content: "For I know the plans I have for you, declares the Lord, plans to prosper you and not to harm you, to give you hope and a future."
    },
    {
      reference: "Psalm 23:1",
      content: "The Lord is my shepherd, I lack nothing."
    },
    {
      reference: "1 Corinthians 13:4-8",
      content: "Love is patient, love is kind. It does not envy, it does not boast, it is not proud. It does not dishonor others, it is not self-seeking, it is not easily angered, it keeps no record of wrongs. Love does not delight in evil but rejoices with the truth. It always protects, always trusts, always hopes, always perseveres. Love never fails."
    },
    {
      reference: "Philippians 4:13",
      content: "I can do all this through him who gives me strength."
    },
    {
      reference: "John 3:16",
      content: "For God so loved the world that he gave his one and only Son, that whoever believes in him shall not perish but have eternal life."
    },
    {
      reference: "Romans 8:28",
      content: "And we know that in all things God works for the good of those who love him, who have been called according to his purpose."
    },
    {
      reference: "Isaiah 41:10",
      content: "So do not fear, for I am with you; do not be dismayed, for I am your God. I will strengthen you and help you; I will uphold you with my righteous right hand."
    },
    {
      reference: "Psalm 46:1",
      content: "God is our refuge and strength, an ever-present help in trouble."
    }
  ];

  // Component state
  verse: WritableSignal<BiblePassage | null> = signal(null);
  isLoading = signal<boolean>(true);
  hasError = signal<boolean>(false);
  errorMessage = signal<string>('');

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.loadVerseOfTheDay();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Load the verse of the day using a daily random seed for consistent daily selection
   * Following API.Bible documentation approach - same verse all day, different each day
   */
  private loadVerseOfTheDay(): void {
    if (!this.API_KEY) {
      this.hasError.set(true);
      this.errorMessage.set('API key not configured');
      this.isLoading.set(false);
      return;
    }

    // Use date-based random seed for daily verse selection
    // This ensures the same verse is shown throughout each day, but changes each day
    const today = new Date();
    const dateSeed = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();
    const verseIndex = dateSeed % this.VERSES.length;
    const verseId = this.VERSES[verseIndex];

    this.getBibleVerse(verseId).subscribe({
      next: (response) => {
        if (response && response.data && response.data.passages && response.data.passages.length > 0) {
          this.verse.set(response.data.passages[0]);
          this.hasError.set(false);
          
          // Send FUMS data as recommended by API.Bible documentation
          this.sendFUMSData(response.meta.fumsId);
        } else {
          // Fall back to static verses if API response is empty
          this.loadStaticVerse();
        }
        this.isLoading.set(false);
      },
      error: (error) => {
        // Fall back to static verses if API fails
        this.loadStaticVerse();
      }
    });
  }

  /**
   * Make API call to Bible API using search endpoint as recommended in documentation
   */
  private getBibleVerse(verseId: string) {
    // Using search endpoint as shown in API.Bible documentation
    const url = `${this.API_BASE_URL}/bibles/${this.BIBLE_ID}/search?query=${encodeURIComponent(verseId)}`;
    
    const headers = new HttpHeaders({
      'api-key': this.API_KEY
    });

    return this.http.get<BibleApiResponse>(url, { headers }).pipe(
      timeout(10000), // 10 second timeout
      catchError(error => {
        // Try passages endpoint as fallback
        return this.getBibleVersePassages(verseId);
      }),
      takeUntil(this.ngUnsubscribe)
    );
  }

  /**
   * Fallback method using passages endpoint
   */
  private getBibleVersePassages(verseId: string) {
    const url = `${this.API_BASE_URL}/bibles/${this.BIBLE_ID}/passages/${encodeURIComponent(verseId)}`;
    
    const headers = new HttpHeaders({
      'api-key': this.API_KEY
    });

    return this.http.get<BibleApiResponse>(url, { headers }).pipe(
      timeout(10000),
      catchError(error => {
        // Return null to trigger static verse fallback
        return of(null as any);
      }),
      takeUntil(this.ngUnsubscribe)
    );
  }

  /**
   * Send FUMS data as required by API.Bible documentation
   * This helps track API usage and ensures fair use compliance
   */
  private sendFUMSData(fumsId: string): void {
    try {
      // Check if FUMS script is available (similar to _BAPI.t() in documentation)
      if (typeof (window as any)._BAPI !== 'undefined' && (window as any)._BAPI.t) {
        (window as any)._BAPI.t(fumsId);
      } else {
        // Silent fallback - no console warning needed
      }
    } catch (error) {
      // Silent fallback - no console warning needed
    }
  }

  /**
   * Load a static verse as fallback when API fails
   */
  private loadStaticVerse(): void {
    // Use date-based random seed for daily static verse selection
    const today = new Date();
    const dateSeed = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();
    const verseIndex = dateSeed % this.STATIC_VERSES.length;
    const staticVerse = this.STATIC_VERSES[verseIndex];
    
    this.verse.set({
      reference: staticVerse.reference,
      content: staticVerse.content
    });
    this.hasError.set(false);
    this.isLoading.set(false);
  }

  /**
   * Retry loading the verse
   */
  retry(): void {
    this.isLoading.set(true);
    this.hasError.set(false);
    this.errorMessage.set('');
    this.loadVerseOfTheDay();
  }

  /**
   * Get the current verse reference for display
   */
  getVerseReference(): string {
    const currentVerse = this.verse();
    return currentVerse ? currentVerse.reference : '';
  }

  /**
   * Get the current verse content for display
   */
  getVerseContent(): string {
    const currentVerse = this.verse();
    return currentVerse ? currentVerse.content : '';
  }

  /**
   * Get the current date for display
   */
  getCurrentDate(): Date {
    return new Date();
  }
}
